<template>
    <x-page title="List Jemaat">
        <template #header-actions>
            <x-create-button
                url="/admin/members/create"
                :disabled="isLoading"
                v-if="can('createMembers')"
            />
            <x-excel-export-button
                :href="excelExportUrl"
                :disabled="isLoading"
            />
            <x-excel-import-button
                v-if="can('createMembers')"
                target="import-excel-modal"
            />
            <x-excel-import-button
                v-if="can('validateMembers')"
                target="validation-modal"
                label="Validate"
            />
            <x-quick-action-button
                v-if="can('updateMembers')"
                url="/admin/members/edit"
                label="Bulk Update"
                icon="pencil"
            />
            <x-quick-action-button
                url="/admin/members/duplicate"
                label="Cari Duplikat"
                icon="search"
            />
        </template>

        <x-filter-box :onFilter="handleFilter" :onReset="resetFilter">
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Nama">
                        <input
                            class="form-control"
                            type="text"
                            v-model="filters.search"
                            @keyup.enter="() => handleFilter()"
                            placeholder="Masukkan nama jemaat"
                        />
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Status">
                        <select class="form-control" v-model="filters.status">
                            <option :value="null">Semua</option>
                            <option
                                v-for="(status, index) in availableStatuses"
                                :value="status"
                                :key="index"
                            >
                                {{ status }}
                            </option>
                        </select>
                    </x-form-group>
                </div>
            </div>
            <div class="row" style="margin-top: 10px">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Kategori Group">
                        <x-ajax-dropdown
                            url="/api/group-categories"
                            :allow-empty="true"
                            v-model="selectedGroupCategory"
                            class="form-control"
                            placeholder="Ketik kategori grup"
                        />
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Group Role">
                        <x-ajax-dropdown
                            url="/api/group-roles"
                            :allow-empty="true"
                            v-model="selectedGroupRole"
                            class="form-control"
                            placeholder="Ketik grup role"
                        />
                    </x-form-group>
                </div>
            </div>
            <div class="row" style="margin-top: 10px">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Usia Minimal">
                        <x-input-group>
                            <input
                                type="number"
                                class="form-control"
                                v-model="filters.age_min"
                                @keyup.enter="() => handleFilter()"
                            />
                            <template #append>Tahun</template>
                        </x-input-group>
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Usia Maksimal">
                        <x-input-group>
                            <input
                                type="number"
                                class="form-control"
                                v-model="filters.age_max"
                                @keyup.enter="() => handleFilter()"
                            />
                            <template #append>Tahun</template>
                        </x-input-group>
                    </x-form-group>
                </div>
            </div>
        </x-filter-box>

        <x-excel-import-modal
            v-if="can('validateMembers')"
            action="/admin/members/validate"
            id="validation-modal"
            submitLabel="Scan"
        />

        <x-excel-import-modal
            v-if="can('createMembers')"
            action="/admin/members/excel-import"
            id="import-excel-modal"
            template-url="/admin/members/excel-export?empty=1"
        >
            <x-form-group
                label="User Login"
                help-text="Secara otomatis menyambungkan jemaat ke user account berdasarkan alamat email"
            >
                <x-checkbox-wrapper label="Sambungkan dengan user account">
                    <input
                        type="checkbox"
                        name="connect_user_account"
                        value="1"
                    />
                </x-checkbox-wrapper>
            </x-form-group>
            <x-form-group
                label="Enable Delete"
                v-if="can('deleteMembers')"
                help-text="Data dengan ID yang tidak terdapat dalam file excel akan dihapus"
            >
                <x-checkbox-wrapper
                    label="Hapus data yang tidak ada dalam excel"
                >
                    <input type="checkbox" name="overwrite" value="1" />
                </x-checkbox-wrapper>
            </x-form-group>
            <x-form-group
                label="Kategori"
                :is-required="false"
                help-text="Kategori default untuk group baru"
            >
                <x-ajax-dropdown
                    url="/api/group-categories"
                    label="label"
                    :allow-empty="true"
                    v-model="defaultGroupCategory"
                    class="form-control"
                />
                <input
                    type="hidden"
                    name="default_group_category_id"
                    :value="
                        defaultGroupCategory ? defaultGroupCategory.id : null
                    "
                />
            </x-form-group>
        </x-excel-import-modal>

        <div class="row">
            <div class="col-xs-6">
                <x-table-count :pagination="meta" />
            </div>
            <div class="col-xs-6">
                <p class="text-right">
                    <router-link to="/admin/members/trashed">
                        {{ trashedCount | number }} dihapus
                    </router-link>
                </p>
            </div>
        </div>

        <x-table
            :headers="[
                '#',
                'ID',
                'Nama Lengkap',
                {
                    label: 'Usia',
                    visible: shouldDisplayAge,
                },
                'Email',
                'Status',
                'Alamat',
                'Jenis Kelamin',
                '',
            ]"
            :is-loading="isLoading"
        >
            <tr v-for="(resource, index) in resources" :key="resource.id">
                <td>{{ rowIndex(index, $route.query) }}</td>
                <td class="nowrap">
                    <router-link
                        :to="`/admin/members/${resource.id}`"
                        title="Klik untuk lihat detail"
                    >
                        {{ resource.id }}
                    </router-link>
                </td>
                <td class="nowrap">
                    <router-link
                        :to="`/admin/members/${resource.id}`"
                        title="Klik untuk lihat detail"
                    >
                        {{ resource.name }}
                    </router-link>
                    <div v-if="Boolean(resource.user_account)">
                        <x-label
                            color="primary"
                            :title="`Tersambung dengan user account ${resource.user_account.email}`"
                        >
                            <i class="fa fa-user" />
                            {{ resource.user_account.name }}
                        </x-label>
                    </div>
                    <img
                        v-if="resource.image_thumb_url"
                        :src="resource.image_thumb_url"
                        class="img-responsive"
                        style="max-height: 100px; margin-top: 5px"
                    />
                </td>
                <td v-if="shouldDisplayAge">
                    {{ resource.age || "-" }}
                </td>
                <td class="nowrap">
                    {{ resource.email }}
                </td>
                <td class="nowrap">
                    {{ resource.status }}
                </td>
                <td class="nowrap">
                    <div v-html="$options.filters.nl2br(resource.address)" />
                </td>
                <td class="nowrap">
                    {{ resource.gender }}
                </td>
                <td>
                    <x-quick-action-button
                        :url="`/admin/members/${resource.id}/move-group`"
                        :disabled="isLoading"
                        v-if="can('updateMembers')"
                        title="Pindah Group"
                        icon="refresh"
                    />
                    <x-quick-edit-button
                        :url="`/admin/members/${resource.id}/edit`"
                        :disabled="isLoading"
                        v-if="can('updateMembers')"
                    />
                    <x-quick-delete-button
                        @click="deleteResource(resource)"
                        :disabled="isLoading"
                        v-if="can('deleteMembers')"
                    />
                </td>
            </tr>
        </x-table>

        <x-pagination
            v-if="shouldRenderPagination"
            :pagination="meta"
            :query="$route.query"
        />
    </x-page>
</template>

<script>
import XActionButton from "../../components/XActionButton.vue";
import XAjaxDropdown from "../../components/XAjaxDropdown.vue";
import XCheckboxWrapper from "../../components/XCheckboxWrapper.vue";
import XCreateButton from "../../components/XCreateButton.vue";
import XQuickDeleteButton from "../../components/XQuickDeleteButton.vue";
import XQuickEditButton from "../../components/XQuickEditButton.vue";
import XExcelExportButton from "../../components/XExcelExportButton.vue";
import XExcelImportButton from "../../components/XExcelImportButton.vue";
import XExcelImportModal from "../../components/XExcelImportModal.vue";
import XFilterBox from "../../components/XFilterBox.vue";
import XFormGroup from "../../components/XFormGroup.vue";
import XLabel from "../../components/XLabel.vue";
import XPage from "../../components/XPage.vue";
import XPagination from "../../components/XPagination.vue";
import XTable from "../../components/XTable.vue";
import {
    availableMemberStatuses,
    deletePrompt,
    handleGeneralXhrError,
} from "../../helpers";
import FilterableMixin from "../../mixins/FilterableMixin";
import PaginationMixin from "../../mixins/PaginationMixin";
import PermissionMixin from "../../mixins/PermissionMixin";
import XQuickActionButton from "../../components/XQuickActionButton.vue";
import XInputGroup from "../../components/XInputGroup.vue";
import XTableCount from "../../components/XTableCount.vue";
import { pickBy } from "lodash";

const defaultFilters = {
    age_min: null,
    age_max: null,
    group_category_id: null,
    group_role_id: null,
    search: null,
    status: null,
};

export default {
    beforeRouteUpdate(to, from, next) {
        this.fetchResources(to);
        next();
    },
    components: {
        XCreateButton,
        XTable,
        XQuickEditButton,
        XQuickDeleteButton,
        XPage,
        XExcelExportButton,
        XExcelImportButton,
        XCheckboxWrapper,
        XFormGroup,
        XExcelImportModal,
        XFilterBox,
        XAjaxDropdown,
        XPagination,
        XLabel,
        XActionButton,
        XQuickActionButton,
        XInputGroup,
        XTableCount,
        XExcelImportButton,
    },
    computed: {
        availableStatuses() {
            return availableMemberStatuses();
        },
        excelExportUrl() {
            const query = pickBy(this.$route.query, (value, key) => {
                return ![
                    "page",
                    "with",
                    "without_global_scopes",
                    "without_trashed",
                ].includes(key);
            });
            const queryString = new URLSearchParams(query).toString();
            return `/admin/members/excel-export?${queryString}`;
        },
        shouldDisplayAge() {
            return true;
        },
    },
    created() {
        this.fetchTrashedCount();
        this.preselectFilters(defaultFilters);
        this.handleFilter().catch(() => {
            this.fetchResources();
        });
    },
    data() {
        return {
            defaultGroupCategory: null,
            filters: { ...defaultFilters },
            isLoading: false,
            resources: [],
            selectedGroupCategory: null,
            selectedGroupRole: null,
            trashedCount: 0,
        };
    },
    methods: {
        deleteResource(resource) {
            deletePrompt({
                name: resource.name,
                onConfirm: () => {
                    this.isLoading = true;
                    const request = axios
                        .delete(`/api/members/${resource.id}`)
                        .then(() => {
                            this.$notify({
                                type: "success",
                                title: `Berhasil`,
                                text: `${resource.name} dihapus!`,
                            });
                            this.fetchResources();
                            this.fetchTrashedCount();
                        });
                    handleGeneralXhrError(request).finally(() => {
                        this.isLoading = false;
                    });
                },
            });
        },
        fetchResources(route = this.$route) {
            this.isLoading = true;
            const request = axios
                .get("/api/members", {
                    params: route.query,
                })
                .then((response) => {
                    this.$set(this, "resources", response.data.data);
                    this.setMeta(response);
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        fetchTrashedCount() {
            this.isLoading = true;
            const request = axios
                .get("/api/members/count", {
                    params: {
                        trashed: 1,
                    },
                })
                .then((response) => {
                    this.trashedCount = response.data.data;
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        handleFilter() {
            return this.$router.push({
                path: this.$route.path,
                query: _.pickBy({
                    ...this.filters,
                    page: 1,
                    with: ["user_account"],
                    without_global_scopes: 1,
                    without_trashed: 1,
                }),
            });
        },
        resetFilter() {
            this.filters = { ...defaultFilters };
            this.handleFilter();
        },
    },
    mixins: [FilterableMixin, PaginationMixin, PermissionMixin],
    watch: {
        "filters.group_category_id": {
            handler(id) {
                this.syncFilterToProperty(
                    id,
                    "selectedGroupCategory",
                    `/api/group-categories/${id}`
                );
            },
            immediate: true,
        },
        "filters.group_role_id": {
            handler(id) {
                this.syncFilterToProperty(
                    id,
                    "selectedGroupRole",
                    `/api/group-roles/${id}`
                );
            },
            immediate: true,
        },
        selectedGroupCategory: {
            handler(resource) {
                this.syncPropertyToFilter(resource, "group_category_id");
            },
            deep: true,
        },
        selectedGroupRole: {
            handler(resource) {
                this.syncPropertyToFilter(resource, "group_role_id");
            },
            deep: true,
        },
    },
};
</script>
